import React, { FC, useEffect, useState } from 'react';
import GetRecords from '../../../../../../../lib/api/GetRecords';
import { APIS } from '../../../../../../../lib/constants/apis';
import { get, cloneDeep, set } from 'lodash';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import AddIcon from '@mui/icons-material/Add';
import {
	addIndexesToPath,
	enumT,
	getKostenKategorieArt,
	getOptionsFromRes,
	removeDataKeys,
	removeNullDeep,
} from '../../../../../../../utils/helper';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';

import Schema from '../../../../../../../lib/paths/zuwendungsantrag_FULL_Paths.json';
import MUIAccordionComponent from '../../../../../../../components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from '../../../../../../../components/ui-component/mui-alert/MUIAlert';
import { concat } from 'lodash';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import { getFormStrucComponentTable } from '../../../../../../../utils/formStruc';
import theme from '../../../../../../../assets/theme/theme';
const P = Schema.attributes;

/**
 *
 * @param kostenposition: Ausgabe / Einnahme
 * @param kostenart: Personalkosten, Sachkosten, etc.
 * @returns
 */
const Kostengruppe: FC<{ kostenposition: string; kostenart: string; kostentraeger: string; unternehmenId?: number }> = ({
	kostenposition,
	kostenart,
	kostentraeger,
	unternehmenId,
}) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [kostenKategories, setKostenKategories] = useState([]);
	const kostenSchema = getKostenSchema(kostenposition);

	const handleAddkosten = () => {
		try {
			let appCopy = cloneDeep(application);
			if (kostenposition === 'Ausgabe') {
				const data = {
					Kostentraeger: kostentraeger,
					Organisation: { id: unternehmenId },
					Ausgabenkategorie: kostenKategories[0],
					Ausgaben_Ausgaben_Block_1: [{ Zelle_1: '' }],
				};
				set(appCopy, `${kostenSchema.path}.${get(application, kostenSchema.path, []).length}`, data);
			} else {
				const data = {
					Kostentraeger: kostentraeger,
					Organisation: { id: unternehmenId },
					Entnahmekategorie: kostenKategories[0],
					Einnahmen_Einnahmen_Block_1: [{ Zelle_1: '' }],
				};
				set(appCopy, `${kostenSchema.path}.${get(application, kostenSchema.path, []).length}`, data);
			}
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Adding Kosten ', err);
		}
	};

	const handleDeleteKosten = async (index: number) => {
		try {
			let appCopy = cloneDeep(application);
			const allKostenPosition = get(appCopy, kostenSchema.path, []);
			set(
				appCopy,
				kostenSchema.path,
				allKostenPosition.filter((_: any, i: number) => i !== index),
			);
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Deleting Kosten ', err);
		}
	};

	const handleChangeKostenTrueFalse = (event: any) => {
		if (event.target.value === 'true') {
			handleAddkosten();
		}
	};

	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = {
					Allgemein_Kostenposition: kostenposition,
					Allgemein_Kostenart_Finanzierung: kostenart,
					Allgemein_Foerderprogramm: { $contains: get(application, P.Foerderprogramm.path + '.id') },
				};
				let res = await GetRecords(APIS.KostenKategories, [], filter);

				setKostenKategories(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategories', err);
			}
		};
		fetch();
	}, [kostenposition, kostenart]);

	return (
		<>
			{get(application, kostenSchema.path, []).map((ele: any, i: number) => {
				const kostenpositions =
					get(ele, 'Organisation.id') === unternehmenId &&
					get(ele, 'Kostentraeger') === kostentraeger &&
					getKostenKategorieArt(get(ele, getKostenKategorie(kostenposition)), kostenKategories) === kostenart;
				if (kostenpositions) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={kostenart}
							onDelete={() => handleDeleteKosten(i)}
							showDeleteButton={!readOnly}
						>
							<Grid2 container spacing={6}>
								<Grid2 sm={6}>
									<CreateForm
										formStruc={mapKosten(kostenposition, formStrucKosten(kostenposition, kostenKategories), i)}
										formValues={application}
										updateFormValuesFunc={setApplicationProperty}
										readOnly={readOnly}
									/>
								</Grid2>
							</Grid2>

							<CreateForm
								formStruc={mapKosten(kostenposition, formStrucErlaeuterungTable(application, kostenposition, i), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={readOnly}
							/>
						</MUIAccordionComponent>
					);
				}
			})}

			{hasKostenpositions(application, kostenposition, kostenart, kostentraeger, unternehmenId, kostenKategories) ? (
				<Button sx={{ mt: 3 }} endIcon={<AddIcon />} onClick={handleAddkosten} disabled={readOnly}>
					{enumT(kostenart)} hinzufügen
				</Button>
			) : (
				[
					readOnly ? (
						<Typography sx={{ mt: 3 }} color="text.secondary">
							keine {enumT(kostenart)} vorhanden
						</Typography>
					) : (
						<FormControl>
							<FormLabel id="antrag-kosten-label" sx={{ mt: 3, color: theme.palette.text.primary }}>
								Liegen {enumT(kostenart)} vor?
							</FormLabel>
							<RadioGroup
								row
								aria-labelledby="antrag-kosten-group-label"
								name="antrag-kosten-buttons-group"
								onChange={handleChangeKostenTrueFalse}
							>
								<FormControlLabel value="true" control={<Radio />} label="Ja" />
								<FormControlLabel value="false" control={<Radio checked />} label="Nein" />
							</RadioGroup>
						</FormControl>
					),
				]
			)}
			{!unternehmenId && (
				<MUIAlert message="Für diesen Partner wurden noch keine Daten ausgefüllt. Bitte tragen Sie die Partner-Daten zuerst ein." />
			)}
		</>
	);
};

export default Kostengruppe;

function getKostenSchema(kostenposition: string) {
	return kostenposition === 'Ausgabe' ? P.Ausgaben : P.Einnahmen;
}

function getKostenSchemaRootName(kostenposition: string) {
	return kostenposition === 'Ausgabe' ? 'Ausgaben' : 'Einnahmen';
}

function getKostenKategorie(kostenposition: string) {
	return kostenposition === 'Ausgabe' ? 'Ausgabenkategorie' : 'Entnahmekategorie';
}

function getKostenPositionsSchema(kostenposition: string) {
	return kostenposition === 'Ausgabe'
		? P.Ausgaben.schema.attributes.Ausgaben_Ausgaben_Block_1
		: P.Einnahmen.schema.attributes.Einnahmen_Einnahmen_Block_1;
}

function getKostenKategorieSchema(kostenposition: string) {
	return kostenposition === 'Ausgabe'
		? P.Ausgaben.schema.attributes.Ausgabenkategorie
		: P.Einnahmen.schema.attributes.Entnahmekategorie;
}

function hasKostenpositions(
	application: any,
	kostenposition: string,
	kostenart: string,
	kostentraeger: string,
	unternehmenId: number | undefined,
	kostenKategories: any,
) {
	let exists = false;
	const positions = get(application, getKostenSchema(kostenposition).path, []);
	for (let position of positions) {
		if (
			get(position, 'Organisation.id') === unternehmenId &&
			get(position, 'Kostentraeger') === kostentraeger &&
			getKostenKategorieArt(get(position, getKostenKategorie(kostenposition)), kostenKategories) === kostenart
		) {
			exists = true;
			break;
		}
	}
	return exists;
}

const mapKosten = (kostenposition: string, formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, getKostenSchema(kostenposition).path.length) +
						`.${index}` +
						pathWithoutIndex.slice(getKostenSchema(kostenposition).path.length),
				};
			}),
		};
	});
	return result;
};

export const formStrucKosten = (kostenposition: string, kostenKategories: any) => {
	return [
		{
			fields: [
				{
					...getKostenKategorieSchema(kostenposition),
					type: 'enumeration',
					options: getOptionsFromRes(kostenKategories, 'Allgemein_Bezeichnung'),
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};

export const formStrucErlaeuterungTable = (application: any, kostenposition: string, i: number) => {
	return [
		{
			fields: [
				{
					...getKostenPositionsSchema(kostenposition),
					type: 'componentTable',
					hideColumns: [
						'Zelle_4',
						...(get(application, P.Antragsstatus_Antragsstatus.path) === 'inBearbeitung' ? ['Zelle_3', 'Zelle_5'] : []),
					],
					disabledColumns: ['Zelle_4', 'Zelle_3', 'Zelle_5'],
					fieldProps: {
						Zelle_2: { format: 'currency', label: 'Geplant (€)' },
						Zelle_3: { format: 'currency', label: 'Bewilligt (€)' },
					},
					sumRow: [
						{
							id: 'pinnedRow',
							Zelle_1: 'Zwischensumme',
							Zelle_2: get(
								application,
								addIndexesToPath(
									getKostenSchema(kostenposition).schema.attributes.Zelle_2.path,
									[`${i}`],
									[getKostenSchemaRootName(kostenposition)],
								),
							),
						},
					],
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
};

export const GetKostenFormStruc = (
	application: any,
	kostenposition: string,
	kostenart: string,
	unternehmenId: number,
	kostentraeger: string,
	kostenKategorie: any,
) => {
	let formStruc: FormStrucItem[] = [];
	get(application, getKostenSchema(kostenposition).path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, 'Organisation.id') === unternehmenId &&
			get(ele, 'Kostentraeger') === kostentraeger &&
			getKostenKategorieArt(get(ele, getKostenKategorie(kostenposition)), kostenKategorie) === kostenart
		) {
			const formStrucCT2WithIndex = getFormStrucComponentTable(
				application,
				mapKosten(kostenposition, formStrucErlaeuterungTable(application, kostenposition, i), i),
			);
			const formStruc1WithIndex = mapKosten(kostenposition, formStrucKosten(kostenposition, []), i);
			formStruc = concat(formStruc, formStruc1WithIndex, formStrucCT2WithIndex);
		}
	});
	return formStruc;
};
